/** @jsxImportSource @emotion/react */
import {css} from "@emotion/react";
import styled from "@emotion/styled";
import DotsHorizontalIcon from "@heroicons/react/solid/DotsHorizontalIcon";
import DownloadIcon from "@heroicons/react/solid/DownloadIcon";
import {PaginatedResult} from "api/common";
import {
  getAllPoliciesDocumentsByDate,
  PolicyDocument,
  PolicyModIds,
} from "api/policy";
import Button from "components/Button";
import {DropDown} from "components/DropDown";
import {Anchor} from "components/elements";
import {
  DateRangeWrapper,
  LoadingIndicator,
  PageContainer,
  ToolbarContainer,
} from "components/listPageUtils";
import Loading from "components/Loading";
import {
  MenuButton,
  MenuButtonItem,
  MenuContainer,
  MenuItems,
} from "components/Menu";
import Pagination from "components/Pagination";
import PolicyNumberCombobox from "components/PolicyNumberCombobox";
import {
  Body,
  Cell,
  Header,
  HeaderCell,
  IconButtonsCellContainer,
  Row,
  Table,
} from "components/Table";
import {TextInput} from "components/TextInput";
import {useApiQuery} from "hooks/api";
import {useNavigation} from "hooks/navigation";
import links from "links";
import {useRef, useState} from "react";
import {UseQueryResult} from "react-query";
import {downloadFile, getFilenameFromUrl} from "utils/dom";
import {formatDate, toTitleCase} from "utils/format";

const options = [
  "Cancellation",
  "Cancellation notice",
  "Earned Premium Invoice",
  "Endorsement",
  "Installment Schedule",
  "Invoice",
  "New Business",
  "Non Renewal Notice",
  "Notice of Cancelation",
  "Pending Cancel (No Notice)",
  "Quotation",
  "Reinstatement",
  "Reminder Notice",
  "Renewal Notice",
  "Renewal Policy",
  "Rescinded Notice",
  "Returned Payment Letter",
];

export interface PoliciesDocumentsPageParams {
  fromDate: string;
  toDate: string;
  policyModIds: PolicyModIds | null;
  insured: string;
  documentDescription: string;
  limit: number;
  pageNumber: number;
}

const PolicyNumberAndInsuredWrapper = styled.div`
  display: flex;

  gap: 6px;
  align-items: center;
`;

const FieldWrapper = styled.div`
  width: 200px;
`;
const LoadingWrapper = styled.div`
  display: flex;
  width: 50%;
  justify-content: flex-end;
  padding-right: 10px;
`;

export default function PoliciesDocuments({
  fromDate,
  toDate,
  policyModIds,
  insured,
  documentDescription,
  limit,
  pageNumber,
}: PoliciesDocumentsPageParams) {
  const {updateSearchParams, removeParams} = useNavigation();
  const policiesDocumentsQuery = useApiQuery(getAllPoliciesDocumentsByDate, {
    keepPreviousData: true,
  })(
    fromDate,
    toDate,
    policyModIds,
    insured,
    documentDescription,
    limit.toString(),
    pageNumber.toString()
  );
  return (
    <PageContainer>
      <ToolbarContainer>
        <DateRangeWrapper>
          <TextInput
            type="date"
            background
            label="From"
            value={fromDate}
            onChange={(e) =>
              updateSearchParams({fromDate: e.target.value, pageNumber: 1})
            }
          />
          <TextInput
            type="date"
            background
            label="To"
            value={toDate}
            onChange={(e) =>
              updateSearchParams({toDate: e.target.value, pageNumber: 1})
            }
          />
        </DateRangeWrapper>
        <LoadingWrapper>
          {policiesDocumentsQuery.isPreviousData && <LoadingIndicator />}
        </LoadingWrapper>
        <PolicyNumberAndInsuredWrapper>
          <FieldWrapper>
            <PolicyNumberCombobox
              label={"Policy Number"}
              selectedOption={
                policyModIds
                  ? {
                      title: policyModIds.display_policy_number,
                      value: policyModIds,
                    }
                  : undefined
              }
              setSelectedOption={(option) => {
                if (option?.value) {
                  updateSearchParams({
                    displayPolicyNumber: option.value.display_policy_number,
                    lob: option.value.lob,
                    companyNumber: option.value.company_number,
                    policyNumber: option.value.policy_number,
                    policyId: option.value.policy_id,
                    pcProviderName: option.value.pc_provider_name,
                    modNumber: option.value.mod_number,
                  });
                } else {
                  removeParams([
                    "displayPolicyNumber",
                    "lob",
                    "companyNumber",
                    "policyNumber",
                    "policyId",
                    "pcProviderName",
                    "modNumber",
                  ]);
                }
              }}
            />
          </FieldWrapper>
          <FieldWrapper>
            <TextInput
              background
              label="Insured"
              value={insured}
              onChange={(e) =>
                updateSearchParams({insured: e.target.value, pageNumber: 1})
              }
            />
          </FieldWrapper>
          <FieldWrapper>
            <DropDown
              background
              label="Document Type"
              options={[
                {title: "All", value: ""},
                ...options.map((item) => ({
                  title: item,
                  value: item,
                })),
              ]}
              value={documentDescription}
              onChange={(selectedOption) =>
                updateSearchParams({
                  documentDescription: selectedOption,
                  pageNumber: 1,
                })
              }
            />
          </FieldWrapper>
        </PolicyNumberAndInsuredWrapper>
      </ToolbarContainer>
      <PoliciesDocumentsList
        fromDate={fromDate}
        toDate={toDate}
        limit={limit}
        policyModIds={policyModIds}
        insured={insured}
        documentDescription={documentDescription}
        pageNumber={pageNumber}
        policiesDocumentsQuery={policiesDocumentsQuery}
      />
    </PageContainer>
  );
}

function PoliciesDocumentsList({
  limit,
  pageNumber,
  policiesDocumentsQuery,
}: PoliciesDocumentsPageParams & {
  policiesDocumentsQuery: UseQueryResult<PaginatedResult<PolicyDocument[]>>;
}) {
  const {navigate} = useNavigation();
  const ref = useRef(null);

  if (policiesDocumentsQuery.data) {
    const policiesDocuments = policiesDocumentsQuery.data.result;
    const totalCount = policiesDocumentsQuery.data.total_count;
    if (policiesDocuments.length === 0) {
      return <p>No documents</p>;
    }

    return (
      <>
        <Table ref={ref} disabled={policiesDocumentsQuery.isPreviousData}>
          <Header>
            <HeaderCell>Upload Date</HeaderCell>
            <HeaderCell>Type</HeaderCell>
            <HeaderCell>Policy Number</HeaderCell>
            <HeaderCell>Insured</HeaderCell>
            <HeaderCell>Office Code</HeaderCell>
            <HeaderCell>Version</HeaderCell>
            <HeaderCell></HeaderCell>
          </Header>
          {policiesDocuments.map((doc) => (
            <Body>
              <Row
                css={css`
                  cursor: pointer;
                `}
                onClick={() => navigate(doc.url, true)}
              >
                <Cell>{formatDate(doc.generated_date)}</Cell>
                <Cell>
                  <Anchor
                    href={doc.url}
                    target="_blank"
                    onClick={(e) => e.preventDefault()}
                  >
                    {doc.type}
                  </Anchor>
                </Cell>
                <Cell>{doc.policy_mod_ids.display_policy_number}</Cell>
                <Cell>{doc.insured_name}</Cell>
                <Cell>{doc.office_code}</Cell>
                <Cell>{toTitleCase(doc.version)}</Cell>
                <Cell>
                  <IconButtonsCellContainer direction="end">
                    <DownloadButton url={doc.url} />
                    <MenuContainer size={"small"}>
                      <MenuButton
                        background={false}
                        icon={DotsHorizontalIcon}
                      />
                      <MenuItems>
                        <MenuButtonItem onClick={() => navigate(doc.url, true)}>
                          View
                        </MenuButtonItem>
                        <MenuButtonItem
                          onClick={() =>
                            downloadFile(
                              doc.url,
                              getFilenameFromUrl(doc.url) || "download"
                            )
                          }
                        >
                          Download
                        </MenuButtonItem>
                        <MenuButtonItem
                          onClick={() =>
                            navigate(
                              links.policy({
                                policyModIds: doc.policy_mod_ids,
                              })
                            )
                          }
                        >
                          View Policy Details
                        </MenuButtonItem>
                      </MenuItems>
                    </MenuContainer>
                  </IconButtonsCellContainer>
                </Cell>
              </Row>
            </Body>
          ))}
        </Table>
        <Pagination
          isCurrentPageLoaded={!policiesDocumentsQuery.isPreviousData}
          scrollOffset={70}
          resultsElementRef={ref}
          pageNumber={pageNumber}
          limit={limit}
          totalCount={totalCount}
        />
      </>
    );
  }

  if (policiesDocumentsQuery.isError) {
    return <p>Error</p>;
  } else {
    return <Loading size="medium" />;
  }
}

function DownloadButton({url}: {url: string}) {
  const [isDownloading, setIsDownloading] = useState(false);
  return (
    <Button
      onClick={() => {
        setIsDownloading(true);
        downloadFile(url, getFilenameFromUrl(url) || "download").finally(() =>
          setIsDownloading(false)
        );
      }}
      isLoading={isDownloading}
      size="small"
      background={false}
      icon={DownloadIcon}
    >
      Download
    </Button>
  );
}
